import React, { useState } from 'react';
import {
  IAuditoriaZeramento13Resultado,
  IItemSaldoZeramento13,
  IItemProvisaoMensalZeramento13,
  IContaConciliadaZeramento13,
  ILancamentoZeramento13,
} from '../interfaces/IAuditoriaZeramento13';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaZeramento13Resultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-284: drill-down a nível de lançamento — todo AVISO/ALERTA/ERRO precisa mostrar não só o
// saldo agregado, mas os lançamentos reais que o compõem.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ lancamentos: ILancamentoZeramento13[] }> = ({ lancamentos }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);

  if (!lancamentos || lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const getLabelCategoria = (categoria: string) => {
  switch (categoria) {
    case 'ADIANT_13':
      return 'Adiantamento de 13º Salário';
    case 'PROV_13_PAGAR':
      return 'Provisão para 13º Salário a Pagar';
    default:
      return categoria;
  }
};

// Retorna duração em meses inteiros entre duas datas (ignora dia).
const duracaoEmMeses = (inicio: Date, fim: Date): number =>
  (fim.getFullYear() - inicio.getFullYear()) * 12 + (fim.getMonth() - inicio.getMonth());

const resolverTituloSaldoFinal = (periodoFim?: string, periodoInicio?: string): string => {
  if (!periodoFim) return 'Saldo Final do Período';
  const fim = new Date(periodoFim);
  if (fim.getMonth() === 11 && fim.getDate() === 31) return 'Saldo Final não Zerado em 31/12';
  if (periodoInicio) {
    const meses = duracaoEmMeses(new Date(periodoInicio), fim);
    if (meses === 0) {
      const label = fim.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
      return `Saldo Final da Competência — ${label.charAt(0).toUpperCase() + label.slice(1)}`;
    }
    if (meses >= 1 && meses <= 2) return 'Saldo Final do Período Selecionado';
  }
  return 'Saldo Final do Período';
};

const resolverDescricaoSaldoFinal = (periodoFim?: string): string => {
  if (!periodoFim) {
    return 'Contas com saldo remanescente ao final do período. Somente encerramento anual (31/12) gera ERRO/AVISO — em períodos intermediários, saldo é esperado e exibido em "Contas Verificadas".';
  }
  const fim = new Date(periodoFim);
  if (fim.getMonth() === 11 && fim.getDate() === 31) {
    return (
      'Contas com saldo remanescente em 31/12 — validar baixa e zeramento de encerramento anual. ' +
      'Provisão para 13º a Pagar: pode gerar ERRO com multa ECD potencial (5% limitada a 1% da receita bruta do período). ' +
      'Adiantamento de 13º não compensado: AVISO sem multa automática (compensação esperada até 20/12). Clique para ver a memória de cálculo.'
    );
  }
  return (
    `Período intermediário (${fim.toLocaleDateString('pt-BR')}) — saldo de Provisão para 13º Salário a Pagar ` +
    'e Adiantamento de 13º são carry-forward esperados durante o exercício. ' +
    'Nenhuma ocorrência nesta seção é normal — consulte "Contas Verificadas" para detalhes. Clique para ver a memória de cálculo.'
  );
};

const resolverTituloSaldoInicial = (periodoInicio?: string): string => {
  if (!periodoInicio) return 'Saldo Inicial do Período';
  const inicio = new Date(periodoInicio);
  if (inicio.getMonth() === 0 && inicio.getDate() === 1) return 'Saldo Inicial não Zerado em 01/01';
  const label = inicio.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
  return `Saldo Inicial do Período — ${label.charAt(0).toUpperCase() + label.slice(1)}`;
};

const resolverDescricaoSaldoInicial = (periodoInicio?: string): string => {
  if (!periodoInicio) {
    return 'Contas com saldo inicial diferente de zero em 01/01 — indica falha de encerramento do exercício anterior. Ambas as categorias geram ALERTA sem multa automática.';
  }
  const inicio = new Date(periodoInicio);
  if (inicio.getMonth() === 0 && inicio.getDate() === 1) {
    return (
      'Contas com saldo inicial diferente de zero em 01/01 — indica falha de encerramento do exercício anterior. ' +
      'Provisão para 13º a Pagar: ALERTA sem multa (multa competiria ao exercício anterior, não ao atual). ' +
      'Adiantamento de 13º: ALERTA sem multa (compensação deveria ter ocorrido até 20/12 do ano anterior, Lei nº 4.749/1965).'
    );
  }
  return (
    `Período intermediário (${inicio.toLocaleDateString('pt-BR')}) — saldo inicial de carry-forward é esperado. ` +
    'Nenhuma ocorrência nesta seção é normal — consulte "Contas Verificadas" para detalhes.'
  );
};

const AuditoriaZeramento13Detalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const saldoFinalNaoZerado = resultado.saldoFinalNaoZerado || [];
  const saldoInicialNaoZerado = resultado.saldoInicialNaoZerado || [];
  const provisaoMensalAusente = resultado.provisaoMensalAusente || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  // COR-66: label e estilo do card de impacto variam conforme há ou não ERRO confirmado.
  // Sem ERRO: "Potencial Estimado" (acompanhamento) — multa só é definitiva com erro confirmado em 31/12.
  const temErroConfirmado = resultado.totalErros > 0;
  const labelImpacto = temErroConfirmado ? 'Impacto Fiscal Estimado' : 'Impacto Potencial Estimado';
  const classeKpiImpacto = temErroConfirmado
    ? 'auditoria-kpi-critico'
    : resultado.impactoFiscalTotal > 0
      ? 'auditoria-kpi-alerta'
      : 'auditoria-kpi-disabled';
  const helpImpacto = temErroConfirmado
    ? 'Multa ECD potencial (5% limitada a 1% da receita bruta do período) calculada sobre saldo remanescente em 31/12. Valor sujeito à confirmação após validação de baixa e encerramento.'
    : 'Estimativa de risco sobre situações em acompanhamento. Sem ERRO confirmado (ex.: saldo em 31/12 não encerrado), não há multa definitiva.';

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Zeramento e Provisão de 13º Salário — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${classeKpiImpacto}`}>
          <div className='auditoria-kpi-label'>{labelImpacto}</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>{helpImpacto}</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Ocorrências de Saldo Final, Saldo Inicial e Provisão Mensal no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada às três análises desta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Saldo Final do Período:</strong> {resultado.fundamentacaoSaldoFinal}</div>
          <div className='mt-2'><strong>Saldo Inicial do Período:</strong> {resultado.fundamentacaoSaldoInicial}</div>
          <div className='mt-2'><strong>Provisão Mensal Ausente:</strong> {resultado.fundamentacaoProvisaoMensal}</div>
        </div>
      )}

      {/* Épico 2 — Tarefa 2.1: Saldo Final do Período */}
      <div className='auditoria-card'>
        <h3>{resolverTituloSaldoFinal(resultado.periodoFim, resultado.periodoInicio)}</h3>
        <p className='text-muted'>{resolverDescricaoSaldoFinal(resultado.periodoFim)}</p>

        {saldoFinalNaoZerado.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma conta com saldo final em aberto identificada no período.</div>
        )}

        {saldoFinalNaoZerado.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Categoria</th>
                  <th>Data Referência</th>
                  <th className='text-end'>Saldo em Aberto</th>
                  <th className='text-end'>Multa</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {saldoFinalNaoZerado.map((item: IItemSaldoZeramento13, idx) => {
                  const chave = `sf-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{getLabelCategoria(item.categoria)}</td>
                        <td>{formatDate(item.dataReferencia)}</td>
                        <td className='text-end'>{formatCurrency(Math.abs(item.saldoNaoZerado))}</td>
                        <td className='text-end'>{formatCurrency(item.multa)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={6} style={{ background: item.status === 'ERRO' ? '#fff5f5' : '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              {item.status === 'AVISO' && (
                                <div className='mt-1 text-muted' style={{ fontSize: 12 }}>
                                  <i className='bi bi-info-circle me-1' />
                                  Adiantamento de 13º não compensado em 31/12 — verificar baixa na quitação do 13º salário. Sem multa automática.
                                </div>
                              )}
                              <BlocoLancamentosRelacionados lancamentos={item.lancamentosRelacionados} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Épico 2 — Tarefa 2.2: Saldo Inicial do Período */}
      <div className='auditoria-card'>
        <h3>{resolverTituloSaldoInicial(resultado.periodoInicio)}</h3>
        <p className='text-muted'>{resolverDescricaoSaldoInicial(resultado.periodoInicio)}</p>

        {saldoInicialNaoZerado.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma conta com saldo inicial não zerado em 01/01 identificada no período.</div>
        )}

        {saldoInicialNaoZerado.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Categoria</th>
                  <th className='text-end'>Saldo Inicial</th>
                  <th className='text-end'>Multa Estimada</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {saldoInicialNaoZerado.map((item: IItemSaldoZeramento13, idx) => {
                  const chave = `si-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{getLabelCategoria(item.categoria)}</td>
                        <td className='text-end'>{formatCurrency(Math.abs(item.saldoNaoZerado))}</td>
                        <td className='text-end'>{formatCurrency(item.multa)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <BlocoLancamentosRelacionados lancamentos={item.lancamentosRelacionados} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contas Conciliadas — visibilidade de que a auditoria rodou e as contas foram verificadas */}
      {(resultado.contasConciliadas || []).length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Contas Verificadas e Conciliadas
          </h3>
          <p className='text-muted'>
            Contas de Adiantamento de 13º Salário e Provisão para 13º Salário a Pagar verificadas sem ocorrências no período.
            O Adiantamento de 13º pode aparecer aqui com saldo em aberto em períodos intermediários — isso é comportamento
            esperado (1ª parcela paga em jun/jul, compensada na quitação do 13º até 20/12, Lei nº 4.749/1965).
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Categoria</th>
                  <th className='text-end'>Saldo Inicial</th>
                  <th className='text-end'>Saldo Final</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(resultado.contasConciliadas || []).map((item: IContaConciliadaZeramento13, idx) => {
                  const chave = `cc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{getLabelCategoria(item.categoria)}</td>
                        <td className='text-end'>{formatCurrency(item.saldoInicial)}</td>
                        <td className='text-end'>{formatCurrency(item.saldoFinal)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <div><i className='bi bi-check-circle-fill text-success me-1' /><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Épico 3 — Tarefa 3.1: Provisão Mensal Ausente */}
      <div className='auditoria-card'>
        <h3>Provisão Mensal de 13º Salário — Despesa Ausente</h3>
        <p className='text-muted'>
          Meses em que houve <strong>novo lançamento de crédito</strong> na Provisão para 13º Salário a Pagar sem o
          correspondente lançamento de despesa de 13º salário no mesmo mês — indício de reconhecimento da obrigação sem
          o encargo de competência (Art. 344 do RIR/2018). Saldo acumulado de meses anteriores é carry-forward esperado
          e não é tratado como ausência.
        </p>

        {provisaoMensalAusente.length === 0 && (
          <div className='text-muted mt-4'>Nenhum mês com crédito de provisão sem despesa correspondente identificado no período.</div>
        )}

        {provisaoMensalAusente.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Competência</th>
                  <th className='text-end'>Crédito de Provisão no Mês</th>
                  <th className='text-end'>Multa Estimada</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {provisaoMensalAusente.map((item: IItemProvisaoMensalZeramento13, idx) => {
                  const chave = `pm-${item.empresaId}-${item.contaContabil}-${item.competencia}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.competencia}</td>
                        <td className='text-end'>{formatCurrency(item.creditoNoMes ?? item.saldoProvisaoNoMes)}</td>
                        <td className='text-end'>{formatCurrency(item.multa)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              {item.saldoInicialMes !== undefined && (
                                <div className='mt-2' style={{ fontSize: 12 }}>
                                  <strong>Breakdown da competência {item.competencia}:</strong>
                                  <table style={{ marginTop: 4, width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                      <tr><td style={{ paddingRight: 16 }}>Saldo inicial:</td><td className='text-end'>{formatCurrency(item.saldoInicialMes)}</td></tr>
                                      <tr><td>Crédito (provisão reconhecida):</td><td className='text-end'><strong>{formatCurrency(item.creditoNoMes)}</strong></td></tr>
                                      <tr><td>Débito (baixa/pagamento):</td><td className='text-end'>{formatCurrency(item.debitoNoMes)}</td></tr>
                                      <tr style={{ borderTop: '1px solid #eee' }}><td>Saldo final:</td><td className='text-end'>{formatCurrency(item.saldoFinalMes)}</td></tr>
                                      <tr><td>Despesa correspondente:</td><td className='text-end text-danger'>Não encontrada</td></tr>
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              <div className='mt-1'><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <BlocoLancamentosRelacionados lancamentos={item.lancamentosRelacionados} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AuditoriaZeramento13Detalhe;
